import React, { useState, useEffect, useRef } from 'react';
import { StyleSheet, css } from 'aphrodite/no-important';
import {
  PopupMenu,
  Button,
  DatePicker,
  TimePicker
} from 'quantux-ui';
import { BASE_URL, FLAG_VALUE, debugLogToConsole } from '../../utils';
import { blue, gray, yellow, salmon } from '../../theme';
import SuccessIcon from '../svg-icons/success-icon';
import WarningIcon from '../svg-icons/warning-icon';
import CriticalWarningIcon from '../svg-icons/critical-warning-icon';
import { prop } from 'ramda';

const TODAY = new Date();

const ProviderPracticeCard = (props) => {
  const [showPracticeMenu, setShowPracticeMenu] = useState(false);
  const [selectedPractice, setSelectedPractice] = useState({});
  const [practices, setPractices] = useState([]);
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);
  // -- Start/Stop menus
  const [showStartMenu, setShowStartMenu] = useState(false);
  const [showStopMenu, setShowStopMenu] = useState(false);
  // -- Start/Stop type
  const [startType, setStartType] = useState(''); // 'anesStart'
  const [stopType, setStopType] = useState(''); // 'anesStop'
  // -- Start/Stop Date and Time
  const [startHour, setStartHour] = useState(TODAY.getHours());
  const [startMinute, setStartMinute] = useState(TODAY.getMinutes());
  const [startDate, setStartDate] = useState(props.date);
  const [startMonth, setStartMonth] = useState(props.month);
  const [startYear, setStartYear] = useState(props.year);
  const [isStartDatePristine, setIsStartDatePristine] = useState(false);
  const [isStartTimePristine, setIsStartTimePristine] = useState(true);
  // --
  const [stopHour, setStopHour] = useState(TODAY.getHours());
  const [stopMinute, setStopMinute] = useState(TODAY.getMinutes());
  const [stopDate, setStopDate] = useState(props.date);
  const [stopMonth, setStopMonth] = useState(props.month);
  const [stopYear, setStopYear] = useState(props.year);
  const [isStopDatePristine, setIsStopDatePristine] = useState(false);
  const [isStopTimePristine, setIsStopTimePristine] = useState(true);
  const [showStopTimeWarning, setShowStopTimeWarning] = useState(false);

  const {
    fullName,
    credentials,
    avatarImageUrl,
    caseRelation
  } = props.data;

  // Refs
  const practiceMenuRef = useRef();
  const startMenuRef = useRef();
  const stopMenuRef = useRef();
  const shouldUpdate = useRef(true);

  /* ---- Effects ----------------------------------------------------  */
  useEffect(() => {
    if (practices.length === 0) {
      // Reading last saved practice from Database
      if (props.data.addedPractices.length > 0 && Object.keys(selectedPractice).length === 0) {
        setSelectedPractice(props.data.addedPractices[0]);
      }
    }

    // debugLogToConsole('🔥🔥🔥 Provider Card --- props.data effect == BEFORE ==');
    if (startType.length === 0 && stopType.length === 0) {
      // debugLogToConsole('🔥🔥🔥 Provider Card --- props.data effect == AFTER ==');
      const {
        startType: startTypeProp,
        startDate: startDateProp,
        startMonth: startMonthProp,
        startYear: startYearProp,
        startHour: startHourProp,
        startMinute: startMinuteProp,
        stopType: stopTypeProp,
        stopDate: stopDateProp,
        stopMonth: stopMonthProp,
        stopYear: stopYearProp,
        stopHour: stopHourProp,
        stopMinute: stopMinuteProp
      } = props.data;

      if (startTypeProp !== undefined && startDateProp !== undefined && startMonthProp !== undefined &&
        startYearProp !== undefined && startHourProp !== undefined && startMinuteProp !== undefined &&
        stopTypeProp !== undefined && stopDateProp !== undefined && stopMonthProp !== undefined &&
        stopYearProp !== undefined && stopHourProp !== undefined && stopMinuteProp !== undefined) {
        shouldUpdate.current = false;
        setStartType(startTypeProp);
        setStartDate(startDateProp);
        setStartMonth(startMonthProp);
        setStartYear(startYearProp);
        setIsStartTimePristine(startTypeProp === 'anesStart');
        setStartHour(startHourProp);
        setStartMinute(startMinuteProp);
        setStopType(stopTypeProp);
        setStopDate(stopDateProp);
        setStopMonth(stopMonthProp);
        setStopYear(stopYearProp);
        setIsStopTimePristine(stopTypeProp === 'anesStop');
        setStopHour(stopHourProp);
        setStopMinute(stopMinuteProp);
        // if (startTypeProp !== startType || startDateProp !== startDate || startMonthProp !== startMonth || startYearProp !== startYear) {}
        // if (stopTypeProp !== stopType || stopDateProp !== stopDate || stopMonthProp !== stopMonth || stopYearProp !== stopYear) {}
      }
    }
  }, [props.data]);

  useEffect(() => {
    if (startType === 'anesStart') {
      if (stopType === 'anesStop') {
        setShowStopTimeWarning(false);
      } else {
        setShowStopTimeWarning(isStopTimePristine);
      }
    } else {
      if (stopType === 'anesStop') {
        setShowStopTimeWarning(false);
      } else {
        if (isStartTimePristine || isStopTimePristine) {
          setShowStopTimeWarning(true);
        } else {
          const start = new Date(startYear, startMonth, startDate, startHour, startMinute).getTime();
          const stop = new Date(stopYear, stopMonth, stopDate, stopHour, stopMinute).getTime();
          setShowStopTimeWarning(stop - start <= 0);
        }
      }
    }
  }, [
    startHour,
    startMinute,
    isStartTimePristine,
    startDate,
    startMonth,
    startYear,
    startType,
    stopHour,
    stopMinute,
    isStopTimePristine,
    stopDate,
    stopMonth,
    stopYear,
    stopType
  ]);

  useEffect(() => {
    if (props.showStartStopTimes) {
      // Determine start flag value
      let startFlag = FLAG_VALUE.success;
      if (startType === 'anesStart') {
        startFlag = FLAG_VALUE.success;
      }
      if (startType === 'setTime' && !isStartTimePristine) {
        startFlag = FLAG_VALUE.success;
      }
      if (startType === 'setTime' && isStartTimePristine) {
        startFlag = FLAG_VALUE.critical;
      }
      // Determine stop flag value
      const stopFlag = showStopTimeWarning ? FLAG_VALUE.critical : FLAG_VALUE.success;
      props.onFlagUpdate(props.index, startFlag, stopFlag);
    }
  }, [
    startType,
    isStartTimePristine,
    showStopTimeWarning
  ]);

  const styles = StyleSheet.create({
    providerPracticeCard: {
      background: blue[700],
      borderRadius: '4px',
      padding: '4px 8px'
    },
    cardHeader: {
      display: showDeleteWarning ? 'none' : '',
      justifyContent: 'space-between',
      height: '40px',
      paddingTop: !props.showPractice ? '4px' : '0'
    },
    headerText: {
      fontSize: '14px',
      lineHeight: '16px'
    },
    headerAvatar: {
      maxHeight: '24px',
      widht: 'auto'
    },
    practiceMenu: {
      display: showDeleteWarning ? 'none' : '',
      position: 'relative'
    },
    searchPopupMenuContainer: {
      width: '150px',
      background: blue[900],
      paddingBottom: '1px',
      borderRadius: '5px',
      overflowY: 'auto',
      maxHeight: '200px'
    },
    menuItem: {
      cursor: 'pointer',
      background: gray[900],
      color: gray[300],
      padding: '4px 8px',
      lineHeight: '16px',
      fontSize: '14px',
      marginTop: '1px',
      height: '32px',
      ':hover': {
        background: '#184878',
        color: 'white'
      },
      ':last-child': {
        borderBottom: '0'
      }
    },
    selectedPractice: {
      display: 'flex',
      alignItems: 'flex-end',
      cursor: 'pointer'
    },
    selectedPracticeItem: {
      maxWidth: '260px',
      fontSize: '14px'
    }
  });

  const handleOnClickPracticeItem = (data) => {
    setSelectedPractice(data);
    setShowPracticeMenu(false);
    props.onAddPractice(data, props.index);
  };

  const resetStartTime = () => {
    setStartHour(TODAY.getHours());
    setStartMinute(TODAY.getMinutes());
    setIsStartTimePristine(true);
    setStartDate(TODAY.getDate());
    setStartMonth(TODAY.getMonth());
    setStartYear(TODAY.getFullYear());
  };

  const resetStopTime = () => {
    setStopHour(TODAY.getHours());
    setStopMinute(TODAY.getMinutes());
    setIsStopTimePristine(true);
    setStopDate(TODAY.getDate());
    setStopMonth(TODAY.getMonth());
    setStopYear(TODAY.getFullYear());
  };

  return (
    <div className={`mt-2 ${css(styles.providerPracticeCard)}`}>
      {/* Delete warning */}
      {showDeleteWarning &&
        <div className='p-2' style={{ width: '100%', height: '100%' }}>
          {/* Warning message */}
          <div className='pt-2'>
            Do you want to delete provider?
          </div>
          <div
            className='fx mt-2 pt-2'
            style={{
              justifyContent: 'flex-end',
              borderTop: `1px solid ${gray[500]}`
            }}
          >
            <Button
              btnType='primary'
              isOutlineBtn
              className='mr-2'
              onClick={() => {
                setShowDeleteWarning(false);
              }}
            >
              Cancel
            </Button>
            <Button
              btnType='warning'
              onClick={() => {
                props.onDeleteProvider(props.index);
              }}
            >
              Continue
            </Button>
          </div>
        </div>
      }

      {/* Header */}
      <div className={`fx ${css(styles.cardHeader)}`}>
        {/* Avatar, FullName and Taxonomy */}
        <div className='fx'>
          <img
            className={css(styles.headerAvatar)}
            src={avatarImageUrl}
          />
          <div className={`ml-2 ${css(styles.headerText)}`}>
            <div style={{ fontWeight: '700' }}>
              <span className='mr-2'>
                {`${fullName}, ${credentials}`}
              </span>
              {/* <SuccessIcon height={14} /> */}
            </div>
            <div>{caseRelation}</div>
          </div>
        </div>
        {/* Delete Button */}
        <div onClick={() => setShowDeleteWarning(true)}>
          <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" fill="none">
            <path fill="#96C8FF" fillRule="evenodd" d="M20.755 3.259a.88.88 0 0 0-.614-.25h-4.313V1.657C15.828.743 15.089 0 14.18 0H6.818C5.91 0 5.171.743 5.171 1.657V3.01H.837A.84.84 0 0 0 0 3.851c0 .466.374.842.837.842h.738v14.65c0 .914.738 1.657 1.647 1.657h14.79c.909 0 1.647-.743 1.647-1.657V4.693h.481c.46 0 .86-.376.86-.842a.83.83 0 0 0-.245-.592ZM2.49 20.675a1.712 1.712 0 0 1-.103-.1c.032.035.067.067.103.1Zm.123.028c.033.036.067.073.1.1-.02-.017-.04-.038-.061-.06l-.04-.04ZM14.174 1.664H6.816v1.361h7.358v-1.36Zm3.835 17.675H3.222V4.69H18.009V19.34ZM9.647 8.362c0-.468.377-.846.842-.846.465 0 .84.378.84.846v7.853a.843.843 0 0 1-.84.846.843.843 0 0 1-.842-.846V8.362Zm-3.715-.846a.843.843 0 0 0-.84.846v7.853c0 .468.375.846.84.846a.843.843 0 0 0 .842-.846V8.362a.844.844 0 0 0-.842-.846Zm8.225.846c0-.468.376-.846.84-.846.466 0 .842.378.842.846v7.853a.843.843 0 0 1-.841.846.843.843 0 0 1-.841-.846V8.362Z" clipRule="evenodd" />
          </svg>
        </div>
      </div>

      {/* Provider Start/Stop Times */}
      {props.showStartStopTimes && !showDeleteWarning &&
        <div>
          {/* Start Time */}
          <div
            ref={startMenuRef}
            className='fx font-14__bold'
            style={{ position: 'relative', color: blue[200] }}
          >
            {/* Title & Flag Icon */}
            <div className='fx fx__v-center mr-2' style={{ width: '50px' }}>
              {startType === 'anesStart' &&
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="11"
                  height="8"
                  fill="none"
                  className='mr-2'
                >
                  <path
                    fill="#00B080"
                    fillRule="evenodd"
                    d="M3.465 6.312 1.03 3.824l-.83.841L3.465 8 10.476.841 9.652 0 3.465 6.312Z"
                    clipRule="evenodd"
                  />
                </svg>
              }
              {startType === 'setTime' && isStartTimePristine &&
                <CriticalWarningIcon className='mr-2' height={9} />
              }
              {startType === 'setTime' && !isStartTimePristine &&
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="11"
                  height="8"
                  fill="none"
                  className='mr-2'
                >
                  <path
                    fill="#00B080"
                    fillRule="evenodd"
                    d="M3.465 6.312 1.03 3.824l-.83.841L3.465 8 10.476.841 9.652 0 3.465 6.312Z"
                    clipRule="evenodd"
                  />
                </svg>
              }
              Start
            </div>
            {/* Selected Start Type */}
            <div
              onClick={() => setShowStartMenu(!showStartMenu)}
              style={{ color: blue[500] }}
              className='cursor__pointer'
            >
              {startType === 'anesStart' && 'Anesthesia Start'}
              {startType === 'setTime' && 'Set Time'}
              <i className='ml-1 font-8 fa fa-chevron-down' />
            </div>
            {showStartMenu &&
              <PopupMenu
                top={25}
                left={-1}
                zIndex='1550'
                position="absolute"
                visible={showStartMenu}
                parent={startMenuRef}
                onOutsideClick={() => { setShowStartMenu(false); }}
              >
                <div className={css(styles.searchPopupMenuContainer)}>
                  <div
                    className={`fx fx__v-center ${css(styles.menuItem)}`}
                    onClick={() => {
                      setStartType('anesStart');
                      setShowStartMenu(false);
                      props.onUpdateStartType(props.index, 'anesStart');
                    }}
                  >
                    Anesthesia Start
                  </div>
                  <div
                    className={`fx fx__v-center ${css(styles.menuItem)}`}
                    onClick={() => {
                      resetStartTime();
                      setStartType('setTime');
                      setShowStartMenu(false);
                      props.onUpdateStartType(props.index, 'setTime');
                    }}
                  >
                    Set Time
                  </div>
                </div>
              </PopupMenu>
            }
            {startType === 'setTime' &&
              <div className='fx'>
                <DatePicker
                  left={-125}
                  right={2}
                  date={startDate}
                  month={startMonth}
                  year={startYear}
                  isPrisitine={isStartDatePristine}
                  color={isStartDatePristine ? yellow[500] : blue[500]}
                  noUnderline
                  className='ml-2 font-14__bold'
                  onChangeDate={(year, month, date) => {
                    setStartDate(date);
                    setStartMonth(month);
                    setStartYear(year);
                    setIsStartDatePristine(false);
                    props.onUpdateStartDate(props.index, year, month, date);
                  }}
                />
                <div className='ml-1 mr-1'>@</div>
                <TimePicker
                  className='font-14__bold'
                  left={-26}
                  hour={startHour}
                  minute={startMinute}
                  color={isStartTimePristine ? yellow[500] : blue[500]}
                  noUnderline
                  timeFormat='24H'
                  timePeriod='AM'
                  isPristine={isStartTimePristine}
                  onChangeTime={(hour, min) => {
                    setStartHour(hour);
                    setStartMinute(min);
                    setIsStartTimePristine(false);
                    props.onUpdateStartTime(props.index, hour, min);
                  }}
                  disableTimeFormat
                />
              </div>
            }
          </div>

          {/* Stop Time */}
          <div
            ref={stopMenuRef}
            className='fx font-14__bold'
            style={{ position: 'relative', color: blue[200] }}
          >
            {/* Title & Flag Icon */}
            <div className='fx fx__v-center mr-2' style={{ width: '50px' }}>
              {showStopTimeWarning
                ? <CriticalWarningIcon className='mr-2' height={9} />
                : <svg xmlns="http://www.w3.org/2000/svg" width="11" height="8" fill="none" className='mr-2'><path fill="#00B080" fillRule="evenodd" d="M3.465 6.312 1.03 3.824l-.83.841L3.465 8 10.476.841 9.652 0 3.465 6.312Z" clipRule="evenodd" />
                </svg>
              }
              Stop
            </div>
            {/* Selected Stop Type */}
            <div
              onClick={() => setShowStopMenu(!showStopMenu)}
              style={{ color: blue[500] }}
              className='cursor__pointer'
            >
              {stopType === 'anesStop' && 'Anesthesia Stop'}
              {stopType === 'setTime' && 'Set Time'}
              <i className='ml-1 font-8 fa fa-chevron-down' />
            </div>
            {showStopMenu &&
              <PopupMenu
                top={25}
                left={-1}
                zIndex='1550'
                position="absolute"
                visible={showStopMenu}
                parent={stopMenuRef}
                onOutsideClick={() => { setShowStopMenu(false); }}
              >
                <div className={css(styles.searchPopupMenuContainer)}>
                  <div
                    className={`fx fx__v-center ${css(styles.menuItem)}`}
                    onClick={() => {
                      setStopType('anesStop');
                      setShowStopMenu(false);
                      props.onUpdateStopType(props.index, 'anesStop');
                    }}
                  >
                    Anesthesia Stop
                  </div>
                  <div
                    className={`fx fx__v-center ${css(styles.menuItem)}`}
                    onClick={() => {
                      resetStopTime();
                      setStopType('setTime');
                      setShowStopMenu(false);
                      props.onUpdateStopType(props.index, 'setTime');
                    }}
                  >
                    Set Time
                  </div>
                </div>
              </PopupMenu>
            }
            {stopType === 'setTime' &&
              <div className='fx'>
                <DatePicker
                  left={-125}
                  right={2}
                  date={stopDate}
                  month={stopMonth}
                  year={stopYear}
                  isPrisitine={isStopDatePristine}
                  color={isStopDatePristine ? yellow[500] : blue[500]}
                  noUnderline
                  className='ml-2 font-14__bold'
                  onChangeDate={(year, month, date) => {
                    setStopDate(date);
                    setStopMonth(month);
                    setStopYear(year);
                    setIsStopDatePristine(false);
                    props.onUpdateStopDate(props.index, year, month, date);
                  }}
                />
                <div className='ml-1 mr-1'>@</div>
                <TimePicker
                  className='pl-1 pr-2 font-14__bold'
                  left={-26}
                  hour={stopHour}
                  minute={stopMinute}
                  color={(isStopTimePristine || showStopTimeWarning) ? yellow[500] : blue[500]}
                  noUnderline
                  timeFormat='24H'
                  timePeriod='AM'
                  isPristine={isStopTimePristine}
                  onChangeTime={(hour, min) => {
                    setStopHour(hour);
                    setStopMinute(min);
                    setIsStopTimePristine(false);
                    props.onUpdateStopTime(props.index, hour, min);
                  }}
                  disableTimeFormat
                />
              </div>
            }
          </div>
        </div>
      }

      {/* Practice List */}
      {props.showPractice &&
        <div
          ref={practiceMenuRef}
          className={css(styles.practiceMenu)}
        >
          {/* Selected Practice */}
          <div
            className={css(styles.selectedPractice)}
            onClick={() => setShowPracticeMenu(true)}
          >
            <div>Practice:</div>
            <div className={css(styles.selectedPracticeItem)}>
              {selectedPractice.practiceName
                ? <div className='ml-2 text-truncate' style={{ color: blue[400] }}>
                  {selectedPractice.practiceName}
                  <i className='ml-2 fa fa-caret-down' />
                </div>
                : <div className='ml-2' style={{ color: props.isDashboard ? yellow[500] : salmon[500] }}>
                  Select
                  <i className='ml-2 fa fa-caret-down' />
                </div>
              }
            </div>
          </div>
          {showPracticeMenu &&
            <PopupMenu
              top={35}
              left={-7}
              zIndex='1550'
              position="absolute"
              visible={showPracticeMenu}
              parent={practiceMenuRef}
              onOutsideClick={() => { setShowPracticeMenu(false); }}
            >
              <div className={css(styles.searchPopupMenuContainer)}>
                {practices.map((practice) => (
                  <div
                    key={practice._id}
                    className={`m-item ${selectedPractice._id === practice._id ? 'active' : ''}`}
                    onClick={() => handleOnClickPracticeItem(practice)}
                  >
                    {practice.practiceName}
                  </div>
                ))}
              </div>
            </PopupMenu>
          }
          {/* Warning text */}
          {Object.keys(selectedPractice).length === 0 &&
            <div className='fx fx__v-center mt-2'>
              {!props.isDashboard
                ? <WarningIcon height={20} />
                : <CriticalWarningIcon height={20} />
              }
              <span className='ml-2'>
                {props.warningText}
              </span>
            </div>
          }
        </div>
      }
    </div>
  );
};

ProviderPracticeCard.defaultProps = {
  showPractice: true,
  showStartStopTimes: true
};
export default ProviderPracticeCard;
